import axios from "axios";
import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  ICreateIdentityProvider,
  IWorkspaceDomain,
  IWorkspaceIdentityProvider,
} from "@/types/workspace";

/**
 * The exact text `IdentityProviderSerializer.validate()` raises for NG-1702's
 * single-active-provider rejection (FR21) — see `cognota/sso/serializers.py`
 * — mirrored here rather than the message the concurrent-activation race
 * reshapes it to (`cognota/core/views.py`'s `_UNIQUE_CONSTRAINT_MESSAGES`),
 * because both are worded identically by design (same failure, two catch
 * sites). No error code exists on either response shape to match on instead.
 */
const SSO_SINGLE_ACTIVE_PROVIDER_REJECTION =
  "Another identity provider is already active for this workspace. Disable it before activating another.";

/**
 * Whether a thrown error is specifically NG-1702's single-active-provider
 * rejection — never a generic "was this a 400" check, which would also match
 * every OTHER validation failure these endpoints can return (an incomplete
 * config, an invalid URL, an unrelated unique-constraint message), each of
 * which is meant to keep showing the existing generic create/update toast.
 *
 * The rejection can reach the client in EITHER of two 400 shapes depending on
 * which check caught it: the normal path raises a bare
 * `serializers.ValidationError` from the serializer's `validate()`, which DRF
 * places under `non_field_errors`; the rarer concurrent-activation race trips
 * the DB's partial-unique constraint instead, caught by `ExceptionHandlerMixin`
 * as an `IntegrityError` and reshaped to `{"error": message}`. Both are
 * checked here rather than assuming only the common path fires.
 */
export function isSsoSingleActiveProviderRejection(err: unknown): boolean {
  if (!axios.isAxiosError(err) || err.response?.status !== 400) return false;
  const data = err.response.data as { error?: string; non_field_errors?: string[] } | undefined;
  const message = (data?.non_field_errors?.[0] ?? data?.error ?? "").trim();
  return message === SSO_SINGLE_ACTIVE_PROVIDER_REJECTION;
}

/**
 * The server's reason for a failed domain-ownership check (NG-3229) — the 400
 * `{ error }` body `DomainVerifyEndpoint` returns when the DNS TXT record was
 * not found. `null` for anything else (a 403, a 5xx, a network failure), so
 * those keep the generic failure copy instead of an empty message.
 */
export function ssoDomainVerifyFailureMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 400) return null;
  const data = err.response.data as { error?: string } | undefined;
  return data?.error?.trim() || null;
}

export class WorkspaceSsoService extends ApiService {
  async getDomains(slug: string, config?: ApiRequestConfig): Promise<IWorkspaceDomain[]> {
    return this.get(`/api/workspaces/${slug}/sso/domains/`, config);
  }

  async createDomain(slug: string, data: { domain: string }): Promise<IWorkspaceDomain> {
    return this.post(`/api/workspaces/${slug}/sso/domains/`, data);
  }

  async updateDomain(
    slug: string,
    domainId: string,
    data: { is_sso_enabled: boolean },
  ): Promise<IWorkspaceDomain> {
    return this.patch(`/api/workspaces/${slug}/sso/domains/${domainId}/`, data);
  }

  async deleteDomain(slug: string, domainId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/sso/domains/${domainId}/`);
  }

  /**
   * Trigger the DNS TXT ownership check for a claimed domain (NG-1705,
   * NG-3229). 200 returns the domain with `verified_at` set; a missing TXT
   * record is a 400 `{ error }` the caller surfaces to the admin.
   */
  async verifyDomain(slug: string, domainId: string): Promise<IWorkspaceDomain> {
    return this.post(`/api/workspaces/${slug}/sso/domains/${domainId}/verify/`);
  }

  async getProviders(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<IWorkspaceIdentityProvider[]> {
    return this.get(`/api/workspaces/${slug}/sso/providers/`, config);
  }

  async createProvider(
    slug: string,
    data: ICreateIdentityProvider,
  ): Promise<IWorkspaceIdentityProvider> {
    return this.post(`/api/workspaces/${slug}/sso/providers/`, data);
  }

  async updateProvider(
    slug: string,
    providerId: string,
    data: Partial<ICreateIdentityProvider>,
  ): Promise<IWorkspaceIdentityProvider> {
    return this.patch(`/api/workspaces/${slug}/sso/providers/${providerId}/`, data);
  }

  async deleteProvider(slug: string, providerId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/sso/providers/${providerId}/`);
  }
}
